import React from 'react';
import {Button, List, Modal, Select, Space, Typography, message} from 'antd';
import {CopyOutlined, DeleteOutlined} from '@ant-design/icons';
import {projectsApi} from '@/api/projects';
import type {
	CreateShareLinkRequest,
	ProjectMemberResponse,
	ProjectRole,
	ShareLinkExpiresIn,
	ShareLinkResponse,
} from '@/types/api';
import {roleLabel} from '@/utils/workspace/projectRole';

const {Text} = Typography;

const ROLE_OPTIONS: {value: Exclude<ProjectRole, 'OWNER'>; label: string}[] = [
	{value: 'VIEWER', label: 'Просмотр'},
	{value: 'EDITOR', label: 'Редактирование'},
	{value: 'ADMIN', label: 'Администрирование'},
];

const EXPIRES_OPTIONS: {value: ShareLinkExpiresIn; label: string}[] = [
	{value: '1h', label: '1 час'},
	{value: '1d', label: '1 день'},
	{value: '7d', label: '7 дней'},
	{value: '30d', label: '30 дней'},
	{value: 'never', label: 'Бессрочно'},
];

type Props = {
	open: boolean;
	projectId: number | null;
	projectName?: string;
	actorRole?: ProjectRole | null;
	onClose: () => void;
};

function shareUrl(token: string): string {
	return `${window.location.origin}/share/${token}`;
}

function formatExpires(expiresAt: string | null): string {
	if (!expiresAt) return 'бессрочно';
	return new Date(expiresAt).toLocaleString();
}

const ShareProjectModal: React.FC<Props> = ({open, projectId, projectName, actorRole, onClose}) => {
	const [members, setMembers] = React.useState<ProjectMemberResponse[]>([]);
	const [links, setLinks] = React.useState<ShareLinkResponse[]>([]);
	const [loading, setLoading] = React.useState(false);
	const [linkRole, setLinkRole] = React.useState<Exclude<ProjectRole, 'OWNER'>>('VIEWER');
	const [expiresIn, setExpiresIn] = React.useState<ShareLinkExpiresIn>('7d');
	const [creating, setCreating] = React.useState(false);

	const grantableRoles = React.useMemo(
		() =>
			ROLE_OPTIONS.filter(option => actorRole === 'OWNER' || option.value !== 'ADMIN'),
		[actorRole]
	);

	const load = React.useCallback(async () => {
		if (!projectId) return;
		setLoading(true);
		const [membersRes, linksRes] = await Promise.all([
			projectsApi.listMembers(projectId),
			projectsApi.listShareLinks(projectId),
		]);
		setLoading(false);
		if (membersRes.error) {
			message.error(membersRes.error.message || 'Не удалось загрузить участников');
		} else {
			setMembers(membersRes.data ?? []);
		}
		if (linksRes.error) {
			message.error(linksRes.error.message || 'Не удалось загрузить ссылки');
		} else {
			setLinks(linksRes.data ?? []);
		}
	}, [projectId]);

	React.useEffect(() => {
		if (open && projectId) {
			void load();
			setLinkRole('VIEWER');
			setExpiresIn('7d');
		}
	}, [open, projectId, load]);

	const copyLink = async (token: string) => {
		try {
			await navigator.clipboard.writeText(shareUrl(token));
			message.success('Ссылка скопирована');
		} catch {
			message.error('Не удалось скопировать ссылку');
		}
	};

	const handleCreate = async () => {
		if (!projectId) return;
		setCreating(true);
		const payload: CreateShareLinkRequest = {role: linkRole, expiresIn};
		const {data, error} = await projectsApi.createShareLink(projectId, payload);
		setCreating(false);
		if (error || !data) {
			message.error(error?.message || 'Не удалось создать ссылку');
			return;
		}
		setLinks(prev => [data, ...prev]);
		await copyLink(data.token);
	};

	const handleRoleChange = async (userId: number, role: Exclude<ProjectRole, 'OWNER'>) => {
		if (!projectId) return;
		const {data, error} = await projectsApi.updateMemberRole(projectId, userId, {role});
		if (error || !data) {
			message.error(error?.message || 'Не удалось изменить роль');
			return;
		}
		setMembers(prev => prev.map(member => (member.userId === userId ? data : member)));
		message.success('Роль обновлена');
	};

	const handleRemove = async (userId: number) => {
		if (!projectId) return;
		const {error} = await projectsApi.removeMember(projectId, userId);
		if (error) {
			message.error(error.message || 'Не удалось отозвать доступ');
			return;
		}
		setMembers(prev => prev.filter(member => member.userId !== userId));
		message.success('Доступ отозван');
	};

	const handleRevokeLink = async (linkId: number) => {
		if (!projectId) return;
		const {error} = await projectsApi.revokeShareLink(projectId, linkId);
		if (error) {
			message.error(error.message || 'Не удалось отозвать ссылку');
			return;
		}
		setLinks(prev => prev.filter(link => link.id !== linkId));
		message.success('Ссылка отозвана');
	};

	return (
		<Modal
			title={projectName ? `Поделиться: ${projectName}` : 'Поделиться'}
			open={open}
			onCancel={onClose}
			footer={null}
			width={640}
			destroyOnClose
		>
			<div style={{display: 'flex', flexDirection: 'column', gap: 24}}>
				<div>
					<Text strong>Кто имеет доступ</Text>
					<List
						style={{marginTop: 8}}
						loading={loading}
						dataSource={members}
						locale={{emptyText: 'Нет участников'}}
						renderItem={member => (
							<List.Item
								actions={
									member.owner || (member.role === 'ADMIN' && actorRole !== 'OWNER')
										? undefined
										: [
												<Select
													key="role"
													size="small"
													value={member.role}
													style={{width: 180}}
													options={grantableRoles}
													onChange={role => void handleRoleChange(member.userId, role)}
												/>,
												<Button
													key="remove"
													type="text"
													danger
													icon={<DeleteOutlined />}
													onClick={() => void handleRemove(member.userId)}
												/>,
											]
								}
							>
								<List.Item.Meta
									title={member.name}
									description={
										member.owner
											? `${member.email} · ${roleLabel(member.role)}`
											: member.email
									}
								/>
							</List.Item>
						)}
					/>
				</div>
				<div>
					<Text strong>Ссылка доступа</Text>
					<Space wrap style={{marginTop: 8, width: '100%'}}>
						<Select
							value={linkRole}
							style={{width: 180}}
							options={grantableRoles}
							onChange={setLinkRole}
						/>
						<Select
							value={expiresIn}
							style={{width: 160}}
							options={EXPIRES_OPTIONS}
							onChange={setExpiresIn}
						/>
						<Button type="primary" loading={creating} onClick={() => void handleCreate()}>
							Создать и скопировать
						</Button>
					</Space>
					<List
						style={{marginTop: 12}}
						loading={loading}
						dataSource={links}
						locale={{emptyText: 'Нет активных ссылок'}}
						renderItem={link => (
							<List.Item
								actions={[
									<Button
										key="copy"
										type="text"
										icon={<CopyOutlined />}
										onClick={() => void copyLink(link.token)}
									/>,
									<Button
										key="revoke"
										type="text"
										danger
										icon={<DeleteOutlined />}
										onClick={() => void handleRevokeLink(link.id)}
									/>,
								]}
							>
								<List.Item.Meta
									title={`${roleLabel(link.role)} · до ${formatExpires(link.expiresAt)}`}
									description={shareUrl(link.token)}
								/>
							</List.Item>
						)}
					/>
				</div>
			</div>
		</Modal>
	);
};

export default ShareProjectModal;
